import { Button, Empty, Input, Skeleton, Tag } from 'antd'
import {
  ExportOutlined,
  FileTextOutlined,
  LeftOutlined,
  RightOutlined,
  SelectOutlined,
} from '@ant-design/icons'
import { useAssistant } from '../AssistantProvider'
import { ArticleReader } from '../ArticleReader'
import { GUIDES, HELP_CENTRE_URL } from '../mockData'

/**
 * Exactly two kinds of thing, and no third:
 *
 *  (a) Walkthroughs. Preppy undocks and flies to the real element. These are
 *      the only help that has to point at the product, so they are the only
 *      help that could not live anywhere else.
 *  (b) Help articles. **Read here**, in a dialog, rather than opened on another
 *      site. An article is nearly always about the screen you are looking at,
 *      and sending you elsewhere to read it costs you your place. The Help
 *      Centre link stays on every article as the thing you send a colleague.
 */

function GuideDetail({ guideId }: { guideId: string }) {
  const { actions } = useAssistant()
  const guide = GUIDES.find((g) => g.id === guideId)

  if (!guide) {
    return (
      <Button size="small" icon={<LeftOutlined />} onClick={() => actions.setOpenGuideId(null)}>
        Guides
      </Button>
    )
  }

  return (
    <div className="space-y-4">
      <Button size="small" icon={<LeftOutlined />} onClick={() => actions.setOpenGuideId(null)}>
        Guides
      </Button>

      <div>
        <h3 className="text-[15px] font-bold leading-snug text-gray-900">{guide.title}</h3>
        <div className="mt-1.5 flex items-center gap-2 text-[10.5px] text-gray-400">
          <Tag color="blue">Walkthrough</Tag>
          <span>{guide.category}</span>
          <span>·</span>
          <span>{guide.minutes}</span>
        </div>
      </div>

      <p className="text-[12.5px] leading-relaxed text-gray-600">{guide.body}</p>

      <Button
        type="primary"
        icon={<SelectOutlined />}
        className="feature-button-solid"
        onClick={() => actions.startWalk(guide.id)}
      >
        Walk me through it
      </Button>

      <div className="text-[9.5px] font-extrabold uppercase tracking-[0.12em] text-gray-400">The steps</div>
      <ol className="rounded-xl border border-gray-200 bg-gray-50 p-3">
        {guide.steps.map((step, index) => (
          <li
            key={step.anchor + index}
            className={`flex gap-2.5 py-2 ${index === guide.steps.length - 1 ? '' : 'border-b border-gray-200'}`}
          >
            <span className="feature-soft-surface feature-soft-text mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold">
              {index + 1}
            </span>
            <span className="text-[12.5px] leading-relaxed text-gray-600">{step.text}</span>
          </li>
        ))}
      </ol>

      <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-500">
        The walkthrough happens on your real screen. The Help Centre version is the one to send to a
        colleague.
      </p>
    </div>
  )
}

export function GuidesTab() {
  const { state, actions } = useAssistant()

  // Outside the early return, so the reader survives a guide opening behind it.
  const openArticle = state.articles.find((a) => a.id === state.openArticleSlug) ?? null
  const reader = (
    <ArticleReader
      article={openArticle}
      articleCount={state.articles.length}
      position={openArticle ? state.articles.indexOf(openArticle) + 1 : 0}
      onClose={() => actions.setOpenArticleSlug(null)}
    />
  )

  if (state.openGuideId) {
    return (
      <>
        <GuideDetail guideId={state.openGuideId} />
        {reader}
      </>
    )
  }

  const query = state.guideQuery.trim().toLowerCase()
  const matches = (text: string) => !query || text.toLowerCase().includes(query)

  // Search the title and category only. The body is prose, so including it
  // makes short queries match everything and the results stop meaning anything.
  // Guides are also filtered to the screen you are on — a walkthrough with
  // nothing to point at is not help, it is a dead end.
  const walkthroughs = GUIDES.filter(
    (g) =>
      g.scopes.includes(state.scope) &&
      (!g.needsAnalysis || state.analysisReady) &&
      (!g.needsRealCall || !state.onSample) &&
      matches(`${g.title} ${g.category}`)
  )
  // Not the body: a two-letter query would match everything. Searching inside
  // articles belongs to Intercom's search endpoint, not a client-side filter.
  const articles = state.articles.filter((a) =>
    matches(`${a.title} ${a.category} ${a.description}`)
  )

  /**
   * Every article currently comes back as `AdvisePrep`, so the label would be
   * ten copies of the name of the panel you are in. Derived rather than switched
   * off, so real collections turn it back on with no deploy.
   */
  const showCategory = new Set(state.articles.map((a) => a.category)).size > 1

  return (
    <div className="space-y-3">
      <Input.Search
        value={state.guideQuery}
        onChange={(event) => actions.setGuideQuery(event.target.value)}
        placeholder="Search guides — try agenda or findings"
        aria-label="Search guides"
        allowClear
      />

      {walkthroughs.length > 0 && (
        <>
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-[9.5px] font-extrabold uppercase tracking-[0.12em] text-gray-400">
              Walkthroughs — on your own screen
            </div>
            <span className="text-[10px] text-gray-400">
              {walkthroughs.length} here
            </span>
          </div>

          {walkthroughs.map((guide) => (
            <div
              key={guide.id}
              className="flex items-center gap-3 rounded-xl border border-l-[3px] border-gray-200 bg-white p-3 transition-colors hover:border-[var(--feature-primary)]"
              style={{ borderLeftColor: 'var(--feature-primary)' }}
            >
              <div className="feature-soft-surface feature-soft-text flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                <SelectOutlined />
              </div>

              <button
                type="button"
                className="min-w-0 flex-1 cursor-pointer border-0 bg-transparent p-0 text-left"
                onClick={() => actions.setOpenGuideId(guide.id)}
              >
                <span className="block text-[12.5px] font-semibold leading-snug text-gray-900">
                  {guide.title}
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[10.5px] text-gray-400">
                  <Tag color="blue" className="m-0">
                    Walkthrough
                  </Tag>
                  <span>{guide.category}</span>
                  <span>·</span>
                  <span>{guide.minutes}</span>
                  <span>·</span>
                  <span>
                    {guide.steps.length} step{guide.steps.length === 1 ? '' : 's'}
                  </span>
                </span>
              </button>

              <Button
                size="small"
                type="primary"
                className="feature-button-solid shrink-0"
                title="Preppy flies to each step on your real screen"
                onClick={() => actions.startWalk(guide.id)}
              >
                Walk me
              </Button>
              <RightOutlined className="shrink-0 text-gray-300" />
            </div>
          ))}
        </>
      )}

      {state.articlesLoading && (
        <>
          <div className="text-[9.5px] font-extrabold uppercase tracking-[0.12em] text-gray-400">
            Help articles
          </div>
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        </>
      )}

      {!state.articlesLoading && articles.length > 0 && (
        <>
          <div className="text-[9.5px] font-extrabold uppercase tracking-[0.12em] text-gray-400">
            Help articles
          </div>
          {articles.map((article) => (
            <button
              key={article.id}
              type="button"
              onClick={() => actions.setOpenArticleSlug(article.id)}
              className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left transition-colors hover:border-[var(--feature-primary)]"
            >
              <div className="feature-soft-surface feature-soft-text flex h-7 w-7 shrink-0 items-center justify-center rounded-lg">
                <FileTextOutlined />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold leading-snug text-gray-900">{article.title}</div>
                <div className="mt-1 text-[10.5px] text-gray-400">
                  {showCategory ? `${article.category} · ` : ''}
                  {article.minutes} read
                </div>
              </div>
              <RightOutlined className="shrink-0 text-gray-300" />
            </button>
          ))}
        </>
      )}

      {walkthroughs.length === 0 && articles.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`Nothing here matches "${state.guideQuery}".`}
          />
          <div className="flex flex-wrap justify-center gap-2">
            {state.hasAnalysis && (
              <Button
                size="small"
                type="primary"
                className="feature-button-solid"
                onClick={() => void actions.sendMessage(state.guideQuery)}
              >
                Ask Preppy instead
              </Button>
            )}
            <Button size="small" href={HELP_CENTRE_URL} target="_blank" rel="noopener noreferrer">
              Search the Help Centre
            </Button>
          </div>
        </div>
      )}

      {/* Distinct from "nothing matched" — those mean different things. */}
      {state.articlesFailed && !state.articlesLoading && (
        <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-500">
          Could not load the help articles just now. The walkthroughs above still work, and the Help
          Centre is one click below.
        </p>
      )}

      <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-500">
        Preppy walks you through the things worth doing on-screen. Everything else lives in the Help
        Centre, so it stays current and you can send a colleague the link.
        <a
          href={HELP_CENTRE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="feature-soft-text mt-2 inline-flex items-center gap-1.5 font-semibold no-underline"
        >
          Browse the full Help Centre <ExportOutlined />
        </a>
      </p>

      {reader}
    </div>
  )
}
